import React, { useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { formatDate } from '@utils/dateTime';
import {
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Progress,
  Select,
  Space,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const INVOICE_STATUS_COLORS = {
  unpaid: 'red',
  partial_paid: 'orange',
  paid: 'green',
  canceled: 'default',
};

const PAYMENT_METHODS = ['bank_transfer', 'check', 'cash', 'other'];

export default function POInvoiceDrawer({ invoice, po, service, onClose, onUpdate, mode = 'view' }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);

  const isCreateMode = mode === 'create';

  const handleCreateInvoice = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const data = {
        invoice_number: values.invoice_number,
        issued_date: values.issued_date?.format('YYYY-MM-DD'),
        due_date: values.due_date?.format('YYYY-MM-DD'),
        total_amount: values.total_amount,
        subtotal: values.subtotal,
        tax_amount: values.tax_amount,
      };
      const res = await service.createInvoice(po.id, data);
      if (res.success) {
        message.success(t('common.submit.success'));
        onUpdate();
        onClose();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      if (error.errorFields) return;
      handleNetworkError(error, { t });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    try {
      const res = await service.cancelInvoice(po.id, invoice.id);
      if (res.success) {
        message.success(t('common.submit.success'));
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    }
  };

  const handleCreatePayment = async () => {
    try {
      const values = await paymentForm.validateFields();
      setPaymentSaving(true);
      const data = {
        amount: values.amount,
        paid_at: values.paid_at?.format('YYYY-MM-DD'),
        payment_method: values.payment_method,
        reference_number: values.reference_number,
        note: values.note,
      };
      const res = await service.createPayment(po.id, invoice.id, data);
      if (res.success) {
        message.success(t('common.submit.success'));
        setShowPaymentForm(false);
        paymentForm.resetFields();
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      if (error.errorFields) return;
      handleNetworkError(error, { t });
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const res = await service.deletePayment(po.id, invoice.id, paymentId);
      if (res.success) {
        message.success(t('common.submit.success'));
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    }
  };

  // --- Create Mode ---
  if (isCreateMode) {
    return (
      <div style={{ width: 320, borderLeft: '1px solid #e8e8e8', background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{t('models.purchase_order.invoice.create')}</strong>
          <CloseOutlined onClick={onClose} style={{ cursor: 'pointer', color: '#888' }} />
        </div>
        <div style={{ padding: '12px 14px', flex: 1, overflowY: 'auto' }}>
          <Form form={form} layout="vertical" size="small" initialValues={{
            issued_date: dayjs(),
            tax_rate: po.tax_rate ? parseFloat(po.tax_rate) * 100 : 0,
          }}>
            <Form.Item name="invoice_number" label={t('models.purchase_order.invoice.number')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <div style={{ display: 'flex', gap: 8 }}>
              <Form.Item name="issued_date" label={t('models.purchase_order.invoice.issued_date')} rules={[{ required: true }]} style={{ flex: 1 }}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="due_date" label={t('models.purchase_order.invoice.due_date')} style={{ flex: 1 }}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item name="total_amount" label={t('models.purchase_order.invoice.total_amount')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0.01} />
            </Form.Item>
            <div style={{ display: 'flex', gap: 8 }}>
              <Form.Item name="subtotal" label={t('models.purchase_order.invoice.subtotal')} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
              <Form.Item name="tax_amount" label={t('models.purchase_order.invoice.tax_amount')} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </div>
          </Form>
        </div>
        <div style={{ padding: '8px 14px', borderTop: '1px solid #e8e8e8', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button size="small" onClick={onClose}>{t('common.button.cancel')}</Button>
          <Button size="small" type="primary" onClick={handleCreateInvoice} loading={saving}>{t('models.purchase_order.invoice.save')}</Button>
        </div>
      </div>
    );
  }

  // --- View Mode ---
  if (!invoice) return null;

  const outstanding = parseFloat(invoice.outstanding_amount || (invoice.total_amount - invoice.paid_amount) || 0);
  const paidPct = invoice.total_amount > 0 ? Math.round((parseFloat(invoice.paid_amount || 0) / parseFloat(invoice.total_amount)) * 100) : 0;
  const canCancel = ['unpaid', 'partial_paid'].includes(invoice.status);

  return (
    <div style={{ width: 320, borderLeft: '1px solid #e8e8e8', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>{invoice.invoice_number}</strong>
        <Space size={8}>
          {canCancel && (
            <Popconfirm
              title={t('models.purchase_order.invoice.cancel_confirm')}
              onConfirm={handleCancel}
              okText={t('common.button.confirm')}
              cancelText={t('common.button.cancel')}
            >
              <span style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 12 }}>
                {t('models.purchase_order.invoice.cancel')}
              </span>
            </Popconfirm>
          )}
          <CloseOutlined onClick={onClose} style={{ cursor: 'pointer', color: '#888' }} />
        </Space>
      </div>

      <div style={{ padding: '10px 14px', flex: 1, overflowY: 'auto', fontSize: 12 }}>
        <Descriptions column={2} size="small" style={{ marginBottom: 10 }}>
          <Descriptions.Item label={t('models.purchase_order.invoice.issued_date')}>
            {formatDate(invoice.issued_date)}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.purchase_order.invoice.due_date')}>
            {invoice.due_date ? (
              <span style={{ color: invoice.due_date < new Date().toISOString().slice(0, 10) && invoice.status !== 'paid' ? '#ff4d4f' : undefined }}>
                {formatDate(invoice.due_date)}
              </span>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.purchase_order.invoice.total_amount')}>
            {parseFloat(invoice.total_amount).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.purchase_order.invoice.status')}>
            <Tag color={INVOICE_STATUS_COLORS[invoice.status]}>
              {t(`models.purchase_order.invoice.statuses.${invoice.status}`)}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
            <span style={{ color: '#888' }}>
              {t('models.purchase_order.invoice.paid_amount')} {parseFloat(invoice.paid_amount || 0).toLocaleString()}
            </span>
            <span style={{ color: '#d48806', fontWeight: 500 }}>
              {t('models.purchase_order.invoice.outstanding')} {outstanding.toLocaleString()}
            </span>
          </div>
          <Progress
            percent={paidPct}
            showInfo={false}
            size="small"
            strokeColor={paidPct >= 100 ? '#52c41a' : '#d48806'}
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>{t('models.purchase_order.payment.records')}</div>
          {invoice.payments?.length > 0 ? (
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 6 }}>
              {invoice.payments.map((pay, idx) => (
                <div
                  key={pay.id}
                  style={{
                    padding: '8px 10px',
                    borderBottom: idx < invoice.payments.length - 1 ? '1px solid #f0f0f0' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{parseFloat(pay.amount).toLocaleString()}</div>
                    <div style={{ color: '#888', fontSize: 11 }}>
                      {t(`models.purchase_order.payment.methods.${pay.payment_method}`, { defaultValue: pay.payment_method })}
                      {' · '}{formatDate(pay.paid_at)}
                    </div>
                    {pay.reference_number && (
                      <div style={{ color: '#aaa', fontSize: 11 }}>Ref: {pay.reference_number}</div>
                    )}
                  </div>
                  <Popconfirm
                    title={t('models.purchase_order.payment.delete_confirm')}
                    onConfirm={() => handleDeletePayment(pay.id)}
                    okText={t('common.button.confirm')}
                    cancelText={t('common.button.cancel')}
                  >
                    <span style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 11 }}>
                      {t('models.purchase_order.payment.delete')}
                    </span>
                  </Popconfirm>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {invoice.status !== 'paid' && invoice.status !== 'canceled' && (
          showPaymentForm ? (
            <div style={{ padding: 12, background: '#f6ffed', borderTop: '2px solid #52c41a', borderRadius: 4 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>{t('models.purchase_order.payment.add')}</div>
              <Form form={paymentForm} layout="vertical" size="small" initialValues={{
                amount: outstanding,
                paid_at: dayjs(),
                payment_method: 'bank_transfer',
              }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Form.Item name="amount" label={t('models.purchase_order.payment.amount')} rules={[{ required: true }]} style={{ flex: 1 }}>
                    <InputNumber style={{ width: '100%' }} min={0.01} max={outstanding} />
                  </Form.Item>
                  <Form.Item name="paid_at" label={t('models.purchase_order.payment.paid_at')} rules={[{ required: true }]} style={{ flex: 1 }}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Form.Item name="payment_method" label={t('models.purchase_order.payment.method')} style={{ flex: 1 }}>
                    <Select>
                      {PAYMENT_METHODS.map((m) => (
                        <Select.Option key={m} value={m}>
                          {t(`models.purchase_order.payment.methods.${m}`)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="reference_number" label={t('models.purchase_order.payment.reference_number')} style={{ flex: 1 }}>
                    <Input />
                  </Form.Item>
                </div>
                <Form.Item name="note" label={t('models.purchase_order.payment.note')}>
                  <Input />
                </Form.Item>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button size="small" onClick={() => { setShowPaymentForm(false); paymentForm.resetFields(); }}>
                    {t('common.button.cancel')}
                  </Button>
                  <Button size="small" type="primary" onClick={handleCreatePayment} loading={paymentSaving}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                    {t('models.purchase_order.payment.confirm')}
                  </Button>
                </div>
              </Form>
            </div>
          ) : (
            <Button type="primary" block size="small" onClick={() => setShowPaymentForm(true)}>
              + {t('models.purchase_order.payment.add')}
            </Button>
          )
        )}
      </div>
    </div>
  );
}
